import { MoveToProjectModal } from '@/components/MoveToProjectModal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { useUpdateTableViewService } from '@/services/tableViews';
import { getDatabaseSourceDisplayName } from '@/pages/TableViews/utils/databaseSources';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FolderPlus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DeleteTableViewModal } from './DeleteTableViewModal';
import { EditTableViewModal } from './EditTableViewModal';

dayjs.extend(relativeTime);

type TableView = {
  id: string;
  name: string;
  templateId: string | null;
  databaseSources: string[];
  createdAt?: Date;
  updatedAt?: Date;
};

const TableViewActionsDropdown = ({ tableViewId }: { tableViewId: string }) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const { updateTableView, isUpdateTableViewPending } =
    useUpdateTableViewService();

  const handleMoveToProject = (projectId: string) => {
    updateTableView(
      { tableViewId, data: { projectId } },
      { onSuccess: () => toast.success('Table view added to project') }
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setIsMoveOpen(true)}>
            <FolderPlus className="h-4 w-4" />
            Add to project
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setIsEditOpen(true)}>
            <Pencil className="h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setIsDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditTableViewModal
        tableViewId={tableViewId}
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
      <DeleteTableViewModal
        tableViewId={tableViewId}
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
      />
      <MoveToProjectModal
        open={isMoveOpen}
        onOpenChange={setIsMoveOpen}
        onSelect={handleMoveToProject}
        isLoading={isUpdateTableViewPending}
      />
    </>
  );
};

export const useTableViewsColumns = (): ColumnDef<TableView>[] => {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <TanstackSortableColumnHeader column={column}>
          <Text variant="body-sm" color="primary">
            Name
          </Text>
        </TanstackSortableColumnHeader>
      ),
      cell: ({ row }) => {
        const navigate = useNavigate();
        const handleRowClick = () => {
          navigate(row.original.id);
        };

        return (
          <FlexRow
            gap="2"
            alignItems="center"
            className="cursor-pointer hover:underline"
            onClick={handleRowClick}
          >
            <Ellipsis className="max-w-[50ch] w-full" asChild hasTooltip>
              <Text variant="body-sm" color="primary">
                {row.original.name}
              </Text>
            </Ellipsis>
          </FlexRow>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <TanstackSortableColumnHeader column={column}>
          <Text variant="body-sm" color="primary">
            Created
          </Text>
        </TanstackSortableColumnHeader>
      ),
      cell: ({ row }) => {
        const navigate = useNavigate();
        const handleRowClick = () => {
          navigate(row.original.id);
        };

        const createdAt = row.original.createdAt;
        return (
          <FlexRow
            gap="2"
            alignItems="center"
            className="cursor-pointer hover:underline"
            onClick={handleRowClick}
          >
            <Text variant="body-sm" color="secondary">
              {createdAt ? dayjs(createdAt).fromNow() : '-'}
            </Text>
          </FlexRow>
        );
      },
    },
    {
      accessorKey: 'databaseSources',
      header: () => {
        return (
          <Text variant="body-sm" color="primary">
            Database(s) used
          </Text>
        );
      },
      cell: ({ row }) => {
        const databaseSources = row.original.databaseSources;
        const displayNames = databaseSources
          .map(source => getDatabaseSourceDisplayName(source))
          .filter((name): name is string => name !== null);

        return (
          <Text variant="body-sm" color="secondary">
            {displayNames.length > 0 ? displayNames.join(', ') : '-'}
          </Text>
        );
      },
    },
    {
      accessorKey: 'actions',
      meta: {
        headerClassName: 'w-10',
        cellClassName: 'w-10',
      },
      header: () => {
        return null;
      },
      cell: ({ row }) => {
        return <TableViewActionsDropdown tableViewId={row.original.id} />;
      },
    },
  ];
};
